import React, { useEffect, useMemo, useState } from "react"
import { plus } from "../hooks/icons"
import { useBeaconResults } from "@app/scripts/providers/beacon-results"
import { BeaconProductResult } from "@app/scripts/lib/beacon"

type ColourSwatchMeta = {
  colour_handle: string
  name: string
  image?: string
  colour?: string
}

export type Swatch = {
  name: string
  image?: string
  colour?: string
  handle: string
  url: string
  published: boolean
}

export type Props = {
  mobile: boolean
  useInteractiveSwatches?: boolean
  swatches: Swatch[]
  currentHandle: string
  currentProductColour: string
  hoveredSiblingHandle?: string | null
  setHoveredSiblingHandle?: (handle: string | null) => void
  swatchColours?: { name: string; hex: string }[]
  beaconResult?: BeaconProductResult | null
}

export function ProductCardSwatches({
  mobile,
  useInteractiveSwatches = false,
  swatches,
  currentHandle,
  currentProductColour,
  hoveredSiblingHandle,
  setHoveredSiblingHandle,
  swatchColours = [],
  beaconResult,
}: Props) {
  const beacon = useBeaconResults()
  const [mobileView, setMobileView] = useState(mobile)

  useEffect(() => {
    if (!mobile) {
      setMobileView(false)
    }
  }, [mobile])

  const formattedSwatches = useMemo(() => {
    if (!swatches?.length) {
      return []
    }

    const swatchMetaobjects = swatchColours || []
    const currentSwatch = swatches.find((s) => s.isCurrentProduct)
    const otherSwatches = swatches.filter((s) => !s.isCurrentProduct)
    const orderedSwatches = currentSwatch
      ? [currentSwatch, ...otherSwatches]
      : swatches

    return orderedSwatches
      .map((swatch) => ({
        ...swatch,
        metaobject: swatchMetaobjects.find(
          (colour: ColourSwatchMeta) =>
            colour.colour_handle?.toLowerCase() ===
            swatch?.colourName?.toLowerCase().replace(/[\s/]+/g, "-")
        ),
      }))
      .filter((swatch) => swatch.metaobject && swatch?.published)
      .slice(0, mobileView ? 4 : 5)
  }, [swatches, swatchColours, mobileView])

  const activeColourHandle = useMemo(() => {
    if (!hoveredSiblingHandle) return currentProductColour?.toLowerCase()
    const activeSwatch = formattedSwatches.find(
      (s) => s.handle === hoveredSiblingHandle
    )
    return (
      activeSwatch?.metaobject?.colour_handle?.toLowerCase() ??
      currentProductColour?.toLowerCase()
    )
  }, [hoveredSiblingHandle, formattedSwatches, currentProductColour])

  if (mobileView && useInteractiveSwatches && swatches?.length <= 1) return null

  return swatches?.length ? (
    <div className="product-colour-swatches__simple">
      {mobileView && !useInteractiveSwatches && swatches?.length > 1 ? (
        <p className="product-colour-swatch__mobile md:is-hidden">
          {swatches.length} Colours +
        </p>
      ) : (
        <div
          className={`product-colour-swatch__icons ${
            mobileView ? "" : "is-hidden"
          } md:is-visible`}
        >
          {formattedSwatches.map((swatch) => (
            <a
              key={swatch.handle}
              href={`/products/${swatch.handle}`}
              onClick={(e) => {
                if (
                  mobileView &&
                  useInteractiveSwatches &&
                  setHoveredSiblingHandle
                ) {
                  e.preventDefault()
                  setHoveredSiblingHandle(swatch.handle)
                  return
                }
                if (!mobileView && beaconResult)
                  beacon.reportClickthrough(beaconResult)
              }}
            >
              {swatch.metaobject.image ? (
                <div
                  className="product-colour-swatch__clickable--image"
                  title={swatch.colourName}
                  data-photo-handle={`${currentHandle}-${swatch.handle}`}
                  onMouseEnter={
                    !mobileView
                      ? () => setHoveredSiblingHandle?.(swatch.handle)
                      : undefined
                  }
                  onMouseLeave={
                    !mobileView
                      ? () => setHoveredSiblingHandle?.(null)
                      : undefined
                  }
                >
                  <img
                    className="product-colour-swatch"
                    src={`${swatch.metaobject?.image}&width=50&height=50`}
                    srcSet={`
    ${swatch.metaobject?.image}&width=50&height=50 1x,
    ${swatch.metaobject?.image}&width=100&height=100 2x
  `}
                    width="50"
                    height="50"
                    alt={swatch.colourName}
                    loading="lazy"
                  />
                  {swatch?.metaobject?.colour_handle?.toLowerCase() ===
                    activeColourHandle && (
                    <span className="product-colour-swatch__underline"></span>
                  )}
                </div>
              ) : swatch.metaobject.colour ? (
                <div
                  className="product-colour-swatch__clickable"
                  data-photo-handle={`${currentHandle}-${swatch.handle}`}
                  title={swatch.colourName}
                  onMouseEnter={
                    !mobileView
                      ? () => setHoveredSiblingHandle?.(swatch.handle)
                      : undefined
                  }
                  onMouseLeave={
                    !mobileView
                      ? () => setHoveredSiblingHandle?.(null)
                      : undefined
                  }
                >
                  <span
                    className="product-colour-swatch"
                    style={{ background: swatch.metaobject.colour }}
                  ></span>
                  {swatch?.metaobject?.colour_handle?.toLowerCase() ===
                    activeColourHandle && (
                    <span className="product-colour-swatch__underline"></span>
                  )}
                </div>
              ) : null}
            </a>
          ))}
          {swatches?.length > (mobileView ? 4 : 5) && (
            <span className="product-colour-swatch__plus">{plus}</span>
          )}
        </div>
      )}
    </div>
  ) : null
}
